import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    OPEN_COOKIE_BANNER_EVENT,
    buildCookiePreferences,
    loadCookiePreferencesWithLegacyMigration,
    saveCookiePreferences,
} from '@/utils/cookiePreferences';

export function CookieConsent() {
    const [isBannerVisible, setIsBannerVisible] = useState(false);
    const { t } = useTranslation();

    const applyPreferences = (analytics: boolean, marketing: boolean) => {
        saveCookiePreferences(buildCookiePreferences(analytics, marketing));
        setIsBannerVisible(false);
    };

    useEffect(() => {
        const handleOpenCookieBanner = () => {
            setIsBannerVisible(true);
        };

        const storedPreferences = loadCookiePreferencesWithLegacyMigration();
        window.addEventListener(OPEN_COOKIE_BANNER_EVENT, handleOpenCookieBanner);

        if (storedPreferences) {
            return () => {
                window.removeEventListener(OPEN_COOKIE_BANNER_EVENT, handleOpenCookieBanner);
            };
        }

        const timer = setTimeout(() => setIsBannerVisible(true), 1500);
        return () => {
            clearTimeout(timer);
            window.removeEventListener(OPEN_COOKIE_BANNER_EVENT, handleOpenCookieBanner);
        };
    }, []);

    const handleAcceptAll = () => {
        applyPreferences(true, true);
    };

    const handleRejectOptional = () => {
        applyPreferences(false, false);
    };

    return (
        <>
            <AnimatePresence>
                {isBannerVisible && (
                    <motion.div
                        initial={{ y: 24, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 16, opacity: 0, transition: { duration: 0.2 } }}
                        transition={{ ease: [0.23, 1, 0.32, 1], duration: 0.3 }}
                        className="fixed bottom-4 left-4 right-4 z-[100] mx-auto md:left-auto md:right-6 md:bottom-6 md:max-w-md"
                    >
                        <div className="rounded-3xl surface-float p-5 text-foreground">
                            <div className="flex items-start gap-4">
                                <div className="hidden">
                                    <Cookie className="h-4 w-4" />
                                </div>

                                <div className="flex-1 space-y-3">
                                    <div className="flex items-start justify-between gap-4">
                                        <h3 className="flex items-center gap-2 text-base font-medium text-foreground">
                                            <Cookie className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                                            {t('cookies.title')}
                                        </h3>
                                        <button
                                            onClick={() => setIsBannerVisible(false)}
                                            className="text-muted-foreground hover:text-foreground transition-colors"
                                            aria-label={t('common.close')}
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>

                                    <p className="text-muted-foreground text-sm leading-relaxed">
                                        {t('cookies.description')}
                                        <Link to="/cookie-policy" className="ml-1 text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground transition-colors">
                                            {t('cookies.learnMore')}
                                        </Link>
                                    </p>

                                    <div className="flex flex-col gap-2 pt-1 sm:flex-row">
                                        <button
                                            onClick={handleAcceptAll}
                                            className="h-9 rounded-lg bg-foreground px-4 text-sm font-medium text-background hover:opacity-90 transition-opacity"
                                        >
                                            {t('cookies.accept')}
                                        </button>
                                        <button
                                            onClick={handleRejectOptional}
                                            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                                        >
                                            {t('cookies.rejectOptional')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
